import { listFilteredEntityIds } from '@/backend/listFilteredEntityIds'
import type { GroupFilter } from '@/modules/Project/Filters/types'
import type { Project } from '@/modules/Projects/useProjects'
import { invariant } from '@/shared/utils/typeAssertions'
import { useQuery } from '@tanstack/vue-query'
import { computed, type Ref } from 'vue'

export function useFieldErrorCountQuery(props: { fileProject: Ref<Project | undefined> }) {
  const failedFieldFilters = computed<GroupFilter | undefined>(() => {
    if (!props.fileProject.value) {
      return
    }

    const filter: GroupFilter = { conjunction: 'or', filters: [] }
    for (const property of props.fileProject.value.properties) {
      filter.filters.push({
        subject: 'field_status',
        matcher: {
          name: 'property_any_of',
          values: ['error'],
          property_id: property.id,
        },
      })

      filter.filters.push({
        subject: 'select_option_value',
        matcher: {
          name: 'property_any_of',
          values: ['invalid'],
          property_id: property.id,
        },
      })
    }

    return filter
  })

  const fileProjectId = computed(() => props.fileProject.value?.id)

  return useQuery({
    queryKey: [fileProjectId, 'field-error-count'],
    enabled: () => !!failedFieldFilters.value && !!props.fileProject.value,
    queryFn: async ({ signal }) => {
      invariant(props.fileProject.value)
      invariant(failedFieldFilters.value)
      const result = await listFilteredEntityIds({
        workspaceId: props.fileProject.value.workspaceId,
        projectId: props.fileProject.value.id,
        filter: failedFieldFilters.value,
        limit: 1,
        requestConfig: { signal },
      })

      if (!result.ok) {
        throw new Error(result.error.message)
      }

      return result.data.metadata.total_count
    },
  })
}
